/**
 * ActionLogSection Component
 */

import React, { useState } from "react";
import styles from "./FlowReport.module.css";
import { StatusBadge } from "./FlowReport";
import { formatDateLocal } from "./FlowReport.utils";

interface ActionLogEntry {
  flowAlias: string;
  flowDisplayName: string;
  action: string;
  status: string;
  message?: string;
  timestamp?: string | number;
}

interface ActionLogSectionProps {
  actionLogs: ActionLogEntry[];
  beautifiedNames?: Record<string, string>;
  reportMode?: "single" | "multi";
}

const MESSAGE_LIMIT = 100;

const getStatusClass = (status: string): string => {
  const s = (status || "").toLowerCase();
  if (s === "success" || s === "accepted") return styles.logStatusSuccess;
  if (["fail", "failure", "error", "rejected"].includes(s)) return styles.logStatusFail;
  if (s === "pending") return styles.logStatusPending;
  return styles.logStatusDefault;
};

/**
 * MessageCell Component - Handles truncation and "Show More"
 */
const MessageCell: React.FC<{ message?: string }> = ({ message }) => {
  const [expanded, setExpanded] = useState(false);

  if (!message || !message.trim()) {
    return <span className={styles.emptyCell}>—</span>;
  }

  const needsTruncation = message.length > MESSAGE_LIMIT;

  if (!needsTruncation) {
    return <span>{message}</span>;
  }

  return (
    <span>
      {expanded ? message : `${message.substring(0, MESSAGE_LIMIT)}...`}
      <button
        className={styles.showMoreBtn}
        onClick={() => setExpanded(!expanded)}
        type="button"
      >
        {expanded ? "Less" : "More"}
      </button>
    </span>
  );
};

const ActionLogSection: React.FC<ActionLogSectionProps> = ({
  actionLogs,
  beautifiedNames = {},
  reportMode = "single",
}) => {
  const showFlowColumn = reportMode === "multi";

  return (
    <div className={styles.actionLogTableSection}>
      <h4 className={styles.sectionTitle}>Action Log</h4>

      {!actionLogs || actionLogs.length === 0 ? (
        <p className={styles.noData}>No action log entries available.</p>
      ) : (
        <table className={`${styles.actionLogTable} ${showFlowColumn ? styles.multiFlowTable : styles.singleFlowTable}`}>
          <colgroup>
            {showFlowColumn && <col className={styles.colFlow} />}
            <col className={styles.colAction} />
            <col className={styles.colStatus} />
            <col className={styles.colMessage} />
            <col className={styles.colTimestamp} />
          </colgroup>
          <thead>
            <tr>
              {showFlowColumn && <th>Flow</th>}
              <th>Action</th>
              <th>Status</th>
              <th>Message</th>
              <th>Timestamp</th>
            </tr>
          </thead>
          <tbody>
            {actionLogs.map((entry, index) => (
              <tr key={`${entry.flowAlias}-${entry.action}-${index}`}>
                {showFlowColumn && (
                  <td>
                    <span className={styles.flowTag}>
                      {entry.flowDisplayName || entry.flowAlias}
                    </span>
                  </td>
                )}
                <td className={styles.logActionCell}>
                  {beautifiedNames[entry.action] || entry.action || "—"}
                </td>
                <td className={styles.logStatusCell}>
                  <span className={`${styles.logStatusBadge} ${getStatusClass(entry.status)}`}>
                    {entry.status || "—"}
                  </span>
                </td>
                <td className={styles.logMessageCell}>
                  <MessageCell message={entry.message} />
                </td>
                <td className={styles.logTimestampCell}>
                  {formatDateLocal(entry.timestamp) || <StatusBadge status={"N/A"} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ActionLogSection;